import {useApolloClient, useMutation} from '@apollo/client';
import {none, some} from '@danwang/option';
import {
  UpdateRawTransactionDocument,
  useAllFeePeriodsQuery,
  useSetBenefitOnRawTransactionsMutation,
  useUpdateRawTransactionsMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import {AmountDisplay} from 'src/AmountDisplay';
import evictAccountRawTransactions from 'src/apolloCache/evictAccountRawTransactions';
import {Button} from 'src/Button';
import {MIXED, PropertyRow, uniform} from 'src/components/BulkEditParts';
import {BenefitPicker} from 'src/CreateTransactionForBenefitModal/BenefitPicker';
import DatePicker from 'src/DatePicker/DatePicker';
import AmountInput from 'src/inputs/AmountInput';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {evictFeePeriodQueries} from 'src/UnlinkBenefitTransactionModal';
import type {Option} from '@danwang/option';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

export type BulkEditRawTransactionsModalUserProps = {
  rawTransactions: BasicRawTransactionFragment[];
  /**
   * Called after a successful save with the rows that changed.
   */
  onSaved: (updated: BasicRawTransactionFragment[]) => void;
};

/**
 * Every editable property starts as "leave unchanged"; a row must be
 * explicitly activated for its value to be applied on save. This makes the
 * modal safe over any selection — "set this field on all of these, touch
 * nothing else" — and with a single row selected it doubles as a full
 * editor.
 */
type PropertyKey =
  | 'account'
  | 'rawDate'
  | 'rawDescription'
  | 'amount'
  | 'benefit';

type BenefitDraft =
  | {kind: 'pick'}
  | {kind: 'link'; benefitId: string; benefitName: string}
  | {kind: 'clear'};

export const BulkEditRawTransactionsModal = (
  props: BulkEditRawTransactionsModalUserProps & SystemProps,
) => {
  const {rawTransactions, onSaved, onRequestClose} = props;
  const formId = React.useId();
  const client = useApolloClient();
  const single = rawTransactions.length === 1 ? rawTransactions[0] : null;

  const {data: feePeriodsData} = useAllFeePeriodsQuery();

  // --- Row-level gates ---------------------------------------------------
  const allManual = rawTransactions.every((rt) => !rt.plaidId && !rt.venmoId);
  const syncedCount = rawTransactions.filter(
    (rt) => rt.plaidId || rt.venmoId,
  ).length;
  const manualOnlyReason = allManual
    ? null
    : `${syncedCount === rawTransactions.length ? 'Synced' : `${syncedCount} of ${rawTransactions.length}`} row${syncedCount === 1 && rawTransactions.length > 1 ? ' is' : 's are'} synced from Plaid/Venmo`;

  const singleConsumed =
    single !== null &&
    single.remainingAmount !== null &&
    single.remainingAmount !== single.amount;
  const amountReason =
    single === null
      ? null
      : !allManual
        ? manualOnlyReason
        : singleConsumed
          ? 'Locked: a transaction consumes this row'
          : single.reconciliation
            ? 'Locked: this row is reconciled'
            : null;

  const uniformAccountId = uniform(rawTransactions, (rt) => rt.account._id);
  const uniformCurrency = uniform(rawTransactions, (rt) => rt.currency);

  const allUsd = rawTransactions.every((rt) => rt.currency === 'USD');
  const anyBenefit = rawTransactions.some((rt) => rt.benefitId != null);

  // --- Draft state --------------------------------------------------------
  const [active, setActive] = React.useState<ReadonlySet<PropertyKey>>(
    new Set(),
  );
  const [account, setAccount] =
    React.useState<Option<AccountListItemFragment>>(none);
  const [rawDate, setRawDate] = React.useState<DateTime>(
    parseDate(rawTransactions[0].date),
  );
  const [rawDescription, setRawDescription] = React.useState('');
  const [amount, setAmount] = React.useState<Option<Money>>(
    single ? some(Money.fromServer(single)) : none,
  );
  const [benefit, setBenefit] = React.useState<BenefitDraft>({kind: 'pick'});
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Linking requires a single account: a benefit's credits are recorded
  // against a specific account, so a cross-account selection has no benefit
  // that is eligible for every row. A staged account move retargets both the
  // gate and the picker — the move applies before the link on save, so
  // eligibility follows the destination, not where the rows sit now.
  // Clearing an existing link needs neither gate.
  const stagedAccountId = active.has('account')
    ? account.map((a) => a._id).getOrElse(null)
    : null;
  const effectiveAccountId = stagedAccountId ?? uniformAccountId;
  const canLinkBenefit = allUsd && effectiveAccountId !== null;
  const benefitReason =
    canLinkBenefit || anyBenefit
      ? null
      : !allUsd
        ? 'Benefits are USD-only'
        : 'Selection spans multiple accounts';

  // --- Current-value summaries -------------------------------------------
  const uniformDate = uniform(rawTransactions, (rt) => rt.date);
  const uniformRawDescription = uniform(
    rawTransactions,
    (rt) => rt.description,
  );
  const uniformBenefit = uniform(
    rawTransactions,
    (rt) => rt.benefit?.name ?? null,
  );
  const accountSummary =
    uniform(rawTransactions, (rt) => rt.account.name) ?? MIXED;

  const isActive = (key: PropertyKey) => active.has(key);
  const toggleRow = (key: PropertyKey) => (next: boolean) => {
    setActive((prev) => {
      const nextSet = new Set(prev);
      if (next) {
        nextSet.add(key);
      } else {
        nextSet.delete(key);
      }
      return nextSet;
    });
    if (!next) {
      // Reverting a staged account move invalidates a benefit picked for
      // the destination — make the user re-pick against the current rows.
      if (key === 'account') {
        setBenefit((prev) => (prev.kind === 'link' ? {kind: 'pick'} : prev));
      }
      return;
    }
    // Activating a row seeds its draft with the current value when the
    // selection agrees on one — with a single row selected this makes every
    // row behave like a plain edit form.
    switch (key) {
      case 'account':
        setAccount(uniformAccountId !== null ? some(rawTransactions[0].account) : none);
        break;
      case 'rawDate':
        setRawDate(parseDate(uniformDate ?? rawTransactions[0].date));
        break;
      case 'rawDescription':
        setRawDescription(uniformRawDescription ?? '');
        break;
      case 'benefit':
        setBenefit(canLinkBenefit ? {kind: 'pick'} : {kind: 'clear'});
        break;
      default:
        break;
    }
  };

  // --- Save ---------------------------------------------------------------
  const [updateRawTransactions] = useUpdateRawTransactionsMutation();
  const [setBenefitOnRawTransactions] = useSetBenefitOnRawTransactionsMutation();
  const [updateSingleRawTransaction] = useMutation(UpdateRawTransactionDocument);

  const rawChanges = {
    account: isActive('account')
      ? account.map((a) => a._id).getOrElse(undefined)
      : undefined,
    description: isActive('rawDescription') ? rawDescription : undefined,
    date: isActive('rawDate') ? rawDate.toISODate() : undefined,
  };
  const hasRawChanges =
    rawChanges.account !== undefined ||
    rawChanges.description !== undefined ||
    rawChanges.date !== undefined;
  const benefitChange: string | null | undefined = !isActive('benefit')
    ? undefined
    : benefit.kind === 'link'
      ? benefit.benefitId
      : benefit.kind === 'clear'
        ? null
        : undefined;
  const amountChange =
    single && isActive('amount')
      ? amount.map((m) => m.amount).getOrElse(undefined)
      : undefined;

  const hasChanges =
    hasRawChanges ||
    benefitChange !== undefined ||
    amountChange !== undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || saving) return;
    setError(null);
    setSaving(true);

    // The save is not one transaction, so order the calls by failure
    // likelihood: the raw-level writes carry the validations the form can't
    // pre-check (cross-currency moves, closed accounts, reconciliation and
    // basis locks), while the patch calls only receive values the form
    // already constrained. Running the likely-rejection first means a failed
    // save normally leaves nothing applied.
    const run = async () => {
      const updatedById = new Map<string, BasicRawTransactionFragment>();
      if (amountChange !== undefined && single) {
        const result = await updateSingleRawTransaction({
          variables: {id: single._id, amount: amountChange},
        });
        const updated = result.data?.updateRawTransaction;
        if (updated) updatedById.set(updated._id, updated);
      }
      if (hasRawChanges) {
        const result = await updateRawTransactions({
          variables: {
            ids: rawTransactions.map((rt) => rt._id),
            account: rawChanges.account,
            description: rawChanges.description,
            date: rawChanges.date,
          },
        });
        for (const updated of result.data?.updateRawTransactions ?? []) {
          updatedById.set(updated._id, updated);
        }
        if (rawChanges.account !== undefined) {
          // Moving rewrites each row's accountId, so cached list pages for
          // every source account and the destination are stale — evict them
          // so they refetch on next view (same reasoning as the Move modal).
          const affectedAccountIds = new Set(
            rawTransactions.map((rt) => rt.account._id),
          );
          affectedAccountIds.add(rawChanges.account);
          affectedAccountIds.forEach((accountId) =>
            evictAccountRawTransactions(client.cache, accountId),
          );
          // A moved row may back a fee period as its charge; the resolver
          // drops the link once accounts mismatch, so cached fee period
          // queries go stale too.
          evictFeePeriodQueries(client.cache);
        }
      }
      if (benefitChange !== undefined) {
        const result = await setBenefitOnRawTransactions({
          variables: {
            ids: rawTransactions.map((rt) => rt._id),
            benefit: benefitChange,
          },
        });
        for (const updated of result.data?.setBenefitOnRawTransactions ?? []) {
          updatedById.set(updated._id, updated);
        }
        // Benefit usage totals hang off fee period queries
        evictFeePeriodQueries(client.cache);
      }
      return [...updatedById.values()];
    };

    run()
      .then((updated) => {
        onRequestClose();
        onSaved(updated);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to save changes.');
      })
      .finally(() => setSaving(false));
  };

  // --- Rendering ----------------------------------------------------------
  const footer = (
    <>
      <Button variant="outline" disabled={saving} onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={!hasChanges || saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="lg"
      // A bottom sheet on a phone, as the transaction sheets are.
      className="top-auto bottom-0 h-auto max-h-[92dvh] rounded-b-none sm:rounded-b-lg"
      header={
        single
          ? 'Edit activity'
          : `Edit ${rawTransactions.length} activity lines`
      }
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <PropertyRow
          label="Account"
          summary={accountSummary}
          active={isActive('account')}
          onToggle={toggleRow('account')}
        >
          <AccountSelect
            selectedAccount={account}
            onSelectAccount={(next) => {
              setAccount(next);
              // A staged link was picked against the previous destination;
              // changing it means re-picking, not silently carrying over.
              setBenefit((prev) =>
                prev.kind === 'link' ? {kind: 'pick'} : prev,
              );
            }}
            currency={uniformCurrency ?? undefined}
          />
        </PropertyRow>
        <PropertyRow
          label="Date"
          summary={uniformDate ?? MIXED}
          active={isActive('rawDate')}
          disabledReason={manualOnlyReason}
          onToggle={toggleRow('rawDate')}
        >
          <DatePicker selectedDate={rawDate} onChange={setRawDate} />
        </PropertyRow>
        <PropertyRow
          label="Description"
          summary={
            uniformRawDescription === null
              ? MIXED
              : uniformRawDescription || <span className="italic">Empty</span>
          }
          active={isActive('rawDescription')}
          disabledReason={manualOnlyReason}
          onToggle={toggleRow('rawDescription')}
        >
          <TextInput
            value={rawDescription}
            onChange={setRawDescription}
            placeholder="Description"
          />
        </PropertyRow>
        {single && (
          <PropertyRow
            label="Amount"
            summary={
              <AmountDisplay variant="plain" amount={Money.fromServer(single)} />
            }
            active={isActive('amount')}
            disabledReason={amountReason}
            onToggle={toggleRow('amount')}
          >
            <AmountInput
              value={amount}
              onChange={setAmount}
              textAlign="left"
              currency={single.currency}
            />
          </PropertyRow>
        )}
        <PropertyRow
          label="Benefit"
          summary={
            uniformBenefit === null
              ? anyBenefit
                ? MIXED
                : 'None'
              : (uniformBenefit ?? 'None')
          }
          active={isActive('benefit')}
          disabledReason={benefitReason}
          onToggle={toggleRow('benefit')}
        >
          <div className="flex flex-col gap-2 pt-1.5">
            {benefit.kind === 'link' ? (
              <div className="flex items-center gap-2 text-sm">
                <span>
                  Link to <span className="font-medium">{benefit.benefitName}</span>
                </span>
                <Button
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setBenefit({kind: 'pick'})}
                >
                  Change
                </Button>
              </div>
            ) : benefit.kind === 'clear' ? (
              <div className="flex items-center gap-2 text-sm">
                <span>The benefit link will be removed</span>
                {canLinkBenefit && (
                  <Button
                    variant="outline"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setBenefit({kind: 'pick'})}
                  >
                    Link instead…
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto">
                  <BenefitPicker
                    feePeriods={feePeriodsData?.allFeePeriods ?? []}
                    transactionDate={Math.max(
                      ...rawTransactions.map((rt) =>
                        parseDate(rt.date).toSeconds(),
                      ),
                    )}
                    accountId={effectiveAccountId ?? undefined}
                    // One pick links every selected row, so "Show all" may
                    // widen dates but never cross accounts here.
                    lockAccount
                    onSelect={(selected) =>
                      setBenefit({
                        kind: 'link',
                        benefitId: selected._id,
                        benefitName: selected.name,
                      })
                    }
                  />
                </div>
                {anyBenefit && (
                  <Button
                    variant="outline"
                    className="h-7 self-start px-2.5 text-xs"
                    onClick={() => setBenefit({kind: 'clear'})}
                  >
                    Clear the current benefit instead
                  </Button>
                )}
              </>
            )}
          </div>
        </PropertyRow>

        {error && <p className="text-destructive text-sm">{error}</p>}
      </form>
    </Modal>
  );
};
